import { FC, useCallback, useState } from 'react';
import { Button, Card, Input, notification, Select, Space, DatePicker } from 'antd';
import dayjs from 'dayjs';

import {
  createAdminAction,
  deleteAdminAction,
  editAdminAction,
  loadAdminsAction,
} from 'store/administration/administration.actions';
import { RegistrationAdminModal } from 'pages/Administration/components/RegistrationAdminModal';
import { AdministrationTable } from 'pages/Administration/components/AdministrationTable';
import { AdministrationFilters, IAdminInfo, IEditAdmin, IRegistrationAdmin } from 'types/administration.types';
import useListData, { LoadListDataParams } from 'hooks/useListData';
import { useAppDispatch } from 'store';
import { roleOptions, ROLES } from 'constants/roles';
import { EditAdministrationModal } from './components/EditAdministrationModal';

const Administration: FC = () => {
  const dispatch = useAppDispatch();
  const [openRegistrationModal, setOpenRegistrationModal] = useState(false);
  const [openEditAdminModal, setOpenEditAdminModal] = useState(false);
  const [editableAdmin, setEditableAdmin] = useState<IAdminInfo>();
  const handleLoadAdmins = useCallback(
    (params: LoadListDataParams) => {
      return dispatch(loadAdminsAction(params));
    },
    [dispatch],
  );

  const { items, loading, pagination, onTableChange, onFiltersChange, filters, loadData } = useListData<
    IAdminInfo,
    AdministrationFilters
  >(handleLoadAdmins);

  const handleOpenRegistrationModal = () => {
    setOpenRegistrationModal(true);
  };
  const handleCloseRegistrationModal = () => {
    setOpenRegistrationModal(false);
  };
  const handleCreateAdmin = (data: Omit<IRegistrationAdmin, 'confirm'>) => {
    return dispatch(createAdminAction(data));
  };
  const handleSuccessCreateAdmin = () => loadData({ filters });
  const handleOpenEditAdminModal = (data: IAdminInfo) => {
    setEditableAdmin(data);
    setOpenEditAdminModal(true);
  };
  const handleCloseEditAdminModal = () => {
    setOpenEditAdminModal(false);
  };
  const handleDeleteAdmin = (data: IAdminInfo) => {
    return dispatch(deleteAdminAction(data))
      .then(handleSuccessEditAdminList)
      .catch((description) =>
        notification.error({
          message: 'Ошибка',
          duration: 2,
          description,
        }),
      );
  };
  const handleEditAdmin = (data: IEditAdmin) => {
    return dispatch(editAdminAction(data));
  };
  const handleSuccessEditAdminList = () => onTableChange(pagination, filters);
  const handleSearch = (search: string) => {
    if ((filters?.search === undefined && !search) || filters?.search === search) return;
    return onFiltersChange({ search });
  };
  const handleChangeRole = (role: ROLES) => onFiltersChange({ role });
  const handleCreatedAtChange = (dates: any) => {
    if (!dates || !dates.length) return [];

    const [start, end] = dates;
    const startDate = start ? dayjs(start).startOf('day') : null;
    const endDate = end ? dayjs(end).endOf('day') : null;

    return onFiltersChange({ createdAt: [startDate, endDate] });
  };

  return (
    <>
      <Card
        title={
          <Space>
            <Input.Search
              loading={loading}
              enterButton="Поиск"
              allowClear
              placeholder="Введите ФИО либо почту"
              style={{
                width: 300,
              }}
              onSearch={handleSearch}
            />
            <Select allowClear placeholder="Выберите роль" options={roleOptions} onChange={handleChangeRole} />
            <div>
              <span>Дата создания: </span>
              <DatePicker.RangePicker allowEmpty={[true, true]} onChange={handleCreatedAtChange} />
            </div>
          </Space>
        }
        extra={
          <>
            <Button type="primary" onClick={handleOpenRegistrationModal}>
              Создать
            </Button>
          </>
        }
      >
        <AdministrationTable
          onDelete={handleDeleteAdmin}
          onEdit={handleOpenEditAdminModal}
          onChange={onTableChange}
          data={items}
          loading={loading}
          pagination={pagination}
        />
      </Card>
      <RegistrationAdminModal
        onCreate={handleCreateAdmin}
        open={openRegistrationModal}
        onHideModal={handleCloseRegistrationModal}
        onSuccess={handleSuccessCreateAdmin}
      />
      {editableAdmin && (
        <EditAdministrationModal
          onSuccess={handleSuccessEditAdminList}
          initialValues={editableAdmin}
          onEdit={handleEditAdmin}
          open={openEditAdminModal}
          onHideModal={handleCloseEditAdminModal}
        />
      )}
    </>
  );
};

export default Administration;
